import {
	type DropOptions,
	type NodeModel,
	isAncestor,
} from "@minoru/react-dnd-treeview";
import { useEffect } from "react";
import { FileProperties } from "../../models/FileProperties";
import { updateAsset } from "../../repositories/ConfigurationAssetRepository";

type Props = {
	asset: NodeModel<FileProperties>[];
	selectedNodes: NodeModel<FileProperties>[];
	isCtrlPressing: boolean;
	setIsCtrlPressing: Function;
	setSelectedNodes: Function;
	setIsDragging: Function;
	onAssetUpdateEnd: (
		result: { ok: true } | { ok: false; message: string },
	) => void;
	onAssetUpdateStart?: () => void;
};

export const useAssetActions = ({
	asset,
	selectedNodes,
	setSelectedNodes,
	isCtrlPressing,
	setIsDragging,
	setIsCtrlPressing,
	onAssetUpdateEnd,
	onAssetUpdateStart = () => {},
}: Props) => {
	useEffect(() => {
		const handleKeyDown = (e: any) => {
			if (e.key.toLowerCase() === "escape") {
				setSelectedNodes([]);
			} else if (e.ctrlKey || e.metaKey) {
				setIsCtrlPressing(true);
			}
		};

		const handleKeyUp = (e: any) => {
			if (e.key.toLowerCase() === "control" || e.key.toLowerCase() === "meta") {
				setIsCtrlPressing(false);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("keyup", handleKeyUp);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("keyup", handleKeyUp);
		};
	}, []);

	const handleDrop = async (
		_: NodeModel<FileProperties>[],
		options: DropOptions<FileProperties>,
	) => {
		let { dropTargetId } = options;

		if (dropTargetId == 0) {
			dropTargetId = 1;
		}

		onAssetUpdateStart();
		const result = await updateAsset(
			selectedNodes.map((cn) => cn.id),
			dropTargetId as number,
		);

		if (result.ok) {
			setSelectedNodes([]);
		}

		onAssetUpdateEnd(result);
	};

	const handleSingleSelect = (node: NodeModel<FileProperties>) => {
		setSelectedNodes([node]);
	};

	const handleMultiSelect = (clickedNode: NodeModel<FileProperties>) => {
		const selectedIds = selectedNodes.map((n) => n.id);

		if (selectedIds.includes(clickedNode.id)) {
			return;
		}
		if (
			selectedIds.some((selectedId) =>
				isAncestor(asset, selectedId, clickedNode.id),
			)
		) {
			return;
		}

		let updateNodes = [...selectedNodes];

		updateNodes = updateNodes.filter((selectedNode) => {
			return !isAncestor(asset, clickedNode.id, selectedNode.id);
		});

		updateNodes = [...updateNodes, clickedNode];
		setSelectedNodes(updateNodes);
	};

	const handleCtrlClick = (
		e: React.MouseEvent,
		node: NodeModel<FileProperties>,
	) => {
		if (e.ctrlKey || e.metaKey) {
			handleMultiSelect(node);
		} else {
			handleSingleSelect(node);
		}
	};

	const handleDragStart = (node: NodeModel<FileProperties>) => {
		const isSelectedNode = selectedNodes.some((n) => n.id === node.id);
		setIsDragging(true);

		if (!isCtrlPressing && isSelectedNode) {
			return;
		}

		if (!isCtrlPressing) {
			setSelectedNodes([node]);
			return;
		}

		if (!selectedNodes.some((n) => n.id === node.id)) {
			setSelectedNodes([...selectedNodes, node]);
		}
	};

	const handleDragEnd = () => {
		setIsDragging(false);
		setIsCtrlPressing(false);
	};
	return { handleDrop, handleCtrlClick, handleDragEnd, handleDragStart };
};
